import type { ItemType } from '../items';
import type { TicketType } from '../ticket-types';
import type { User } from '../users';

export type FlagColor =
	| 'DARK_ORCHID'
	| 'DARK_RED'
	| 'DEEP_SKY_BLUE'
	| 'FOREST_GREEN'
	| 'GAINSBORO'
	| 'GOLD'
	| 'GREEN_YELLOW'
	| 'HOT_PINK'
	| 'INDIGO'
	| 'LIGHT_SALMON'
	| 'LIME_GREEN'
	| 'ORANGE'
	| 'ORANGE_RED'
	| 'PERU'
	| 'ROYAL_BLUE'
	| 'SLATE_GRAY'
	| 'TEAL';

export type Flag = {
	id: string;
	name: string;
	available: boolean;
	color: FlagColor;
	description: string;
};

export type ColoredLabel = {
	id: string;
	name: string;
	color: string;
	default: boolean;
	ticketTypeId: string;
};

enum DayOfWeek {
	MONDAY = 0,
	TUESDAY = 1,
	WEDNESDAY = 2,
	THURSDAY = 3,
	FRIDAY = 4,
	SATURDAY = 5,
	SUNDAY = 6,
}

export type OpeningHour = {
	dayOfWeek: DayOfWeek;
	openTime: number;
	closeTime: number;
};

export type HolidayRecurrence = 'NEVER' | 'YEARLY';

export interface Holiday {
	id: string;
	name: string;
	recurrence: HolidayRecurrence;
	start: string;
	end: string;
	validFrom: string;
	validTo?: string | null;
}

/**
 * Payload shape for setClosedDays (whole-list replace): existing entries keep
 * their `id` so the backend can match them; new entries omit `id` entirely so
 * the backend assigns one.
 */
export type HolidayInput = Omit<Holiday, 'id'> & { id?: string };

interface BookingSettingsDuration {
	duration: string;
	skipClosedDays: boolean;
}

export interface BookingSettings {
	bookingLimit: number | null;
	timeOptionInterval: number | null;
	bufferTime: number | null;
	bufferTimeUnit: 'minutes' | 'hours' | 'days' | null;
	bufferTimeSkipOpeningHours: boolean;
	minimumBookingDuration: BookingSettingsDuration | null;
	maximumBookingDuration: BookingSettingsDuration | null;
	minimumInAdvance: BookingSettingsDuration | null;
	maximumInAdvance: BookingSettingsDuration | null;
	createdOn?: string;
	modifiedOn?: string;
}

interface CleanupSettings {
	archiveOrdersClosed?: number;
	deleteOrdersCreating?: number;
	cancelReservationsOverdue?: number;
	cancelReservationsPassed?: number;
	closeReservationsOverdue?: number;
	closeReservationsPassed?: number;
	archiveReservationsClosed?: number;
	archiveReservationsCancelled?: number;
	deleteReservationsCreating?: number;
	archiveSpotchecksClosed?: number;
	closeSpotchecksOpen?: number;
	deleteSpotchecksOpen?: number;
}

interface SubscriptionLimits {
	maxItems: number;
	maxItemsSoft: number;
	maxUsers: number;
	maxLocations: number;
	maxUsersPerSync: number;
	maxSMSPerMo: number;
	maxFileUpload: number;
	maxWorkspaces: number;
	maxBulkItems: number;
	maxCustomItems: number;
	maxCustomTicketTypes: number;
	allowConsumables: boolean;
	allowGeo: boolean;
	allowFlags: boolean;
	allowOrders: boolean;
	allowReservations: boolean;
	allowReservationsRepeat: boolean;
	allowTickets: boolean;
	allowGeneratePdf: boolean;
	allowSelfService: boolean;
	allowKits: boolean;
	allowRestrictLocations: boolean;
	allowCustody: boolean;
	allowSendMessage: boolean;
	allowUserSync: boolean;
	allowWebhooks: boolean;
	allowNotifications: boolean;
	allowNotificationsEmail: boolean;
	allowNotificationsPush: boolean;
	allowNotificationsSMS: boolean;
	allowIntegrationSlack: boolean;
	allowBlockContacts: boolean;
	allowBusinessHours: boolean;
	allowBookingRestrictions: boolean;
	allowEquipmentPicking: boolean;
	allowSpotcheck: boolean;
	allowAPI: boolean;
	allowSSO: boolean;
	allowCustomRoles: boolean;
	allowICal: boolean;
	allowImport: boolean;
	allowRFID: boolean;
	allowExport: boolean;
	allowActivityFeed: boolean;
	allowSupportChat: boolean;
	allowLabels: boolean;
	allowLocationBusinessHours: boolean;
	allowHolidays: boolean;
	allowEquipmentAccess: boolean;
	allowUserGroups: boolean;
	allowLocationHierarchy: boolean;
	allowBookingPortal: boolean;
	allowDepreciations: boolean;
}

interface SubscriptionPlan {
	_id: string;
	name: string;
	family: string;
	price: number;
	priceFriendly: string;
	groupKey: string;
	recurlyCode: string;
	limits: SubscriptionLimits;
}

type SubscriptionKind = 'fromto' | 'trial' | 'recurly';

type SubscriptionStatus =
	| 'active'
	| 'expired'
	| 'cancelled_expired'
	| 'cancelled_not_expired'
	| 'archived'
	| 'not_started'
	| 'not_activated'
	| 'inactive_other';

interface SubscriptionPending {
	renewalSubscriptionPlan: SubscriptionPlan;
	recurlyPrice: number;
	recurlyIsListPrice: boolean;
}

interface Subscription {
	kind: SubscriptionKind;
	status: SubscriptionStatus | null;
	customized: boolean;
	recurlyId: string;
	recurlyStatus: string;
	recurlyPlan: string;
	recurlyChecked: string;
	recurlyExpiresAt: string;
	subscriptionPlan: SubscriptionPlan;
	pending: SubscriptionPending | null;
	recurlyCurrentPeriodEndsAt: string;
	validFrom: string;
	validTo: string;
	trialValidTo: string;
	trialValidFrom: string;
	trialExpired: boolean;
}

type RoundType = 'shorter' | 'longer' | 'nearest';
type PaperSize = 'LETTER' | 'A4' | 'LEGAL';

interface GroupProfile {
	autoUpdateGeo: boolean;
	autoGenerateCodes: boolean;
	currency: string;
	timeFormat24: boolean;
	dateFormat: string;
	timeFormat: string;
	useHours: boolean;
	avgCheckoutHours: number;
	roundMinutes: number;
	roundType: RoundType;
	weekStart: number;
	customQrCodePrefix: string;
	customQrCodePrefixes: string[];
	useFlags: boolean;
	useLocations: boolean;
	useGeo: boolean;
	useOrders: boolean;
	useReservations: boolean;
	useReservationsClose: boolean;
	useReservationsRepeat: boolean;
	useTickets: boolean;
	useKits: boolean;
	useCustody: boolean;
	useSelfService: boolean;
	usePublicSelfService: boolean;
	usePublicInventory: boolean;
	useOrderAgreements: boolean;
	useUserSync: boolean;
	useReporting: boolean;
	useRestrictLocations: boolean;
	useDepreciations: boolean;
	useWebhooks: boolean;
	useNotifications: boolean;
	useNotificationsEmail: boolean;
	useNotificationsPush: boolean;
	useIntegrationSlack: boolean;
	useIntegrationSMS: boolean;
	useBlockContacts: boolean;
	useSpotcheck: boolean;
	useEquipmentPicking: boolean;
	useAPI: boolean;
	useActivityFeed: boolean;
	useLabels: boolean;
	useQuickCheckout: boolean;
	useRFID: boolean;
	useLockedKits: boolean;
	usePastOrders: boolean;
	useBlockEditOfOpenOrders: boolean;
	usePastReservations: boolean;
	useBlockEditOfOpenReservations: boolean;
	useSendMessage: boolean;
	useOrderTransfers: boolean;
	useTransits: boolean;
	useActivities: boolean;
	flagMustMessage: boolean;
	flagMustAttach: boolean;
	custodyCanChangeLocation: boolean;
	forceCheckListCheckin: boolean;
	orderCounter: number;
	orderCounterFormat: string;
	reservationCounter: number;
	reservationCounterFormat: string;
	orderCheckinLocation: boolean;
	orderAgreementCounter: number;
	orderAgreementFormat: string;
	orderAgreementSize: PaperSize;
	orderAgreementTemplate: string;
}

export enum CustomFieldKind {
	string = 'string',
	int = 'int',
	float = 'float',
	date = 'date',
	datetime = 'datetime',
	select = 'select',
	boolean = 'boolean',
	customer = 'customer',
	location = 'location',
}

export const CustomFieldEditorValues = [
	'phone',
	'text',
	'date',
	'datetime',
	'currency',
	'number',
	'url',
	'email',
	'location',
	'category',
	'multiple',
	'boolean',
	'full_address',
] as const;
export type CustomFieldEditor = (typeof CustomFieldEditorValues)[number];

export interface UserRoleBlacklistEntry {
	role: string;
	denyRead: boolean;
	denyWrite: boolean;
}

export interface CustomField {
	id: string;
	name: string;
	description?: string;
	kind: CustomFieldKind;
	required: boolean;
	form: boolean;
	search: boolean;
	select?: string[];
	editor?: CustomFieldEditor;
	autocomplete: boolean;
	unique: boolean;
	unit?: string | null;
	value?: string;
	basicField?: boolean;
	allowedComparators?: string[];
	internal?: boolean;
	userRoleBlacklist?: UserRoleBlacklistEntry[];
}

export type RoleScope = 'workspace' | 'booking_portal';

export type Role = {
	_id: string;
	name: string;
	description: string;
	permissions: string[];
	system: boolean;
	scope: RoleScope[];
};

export interface Group {
	_id: string;
	name: string;
	db: string;
	admin: User;
	owner: User;
	organisationId: string;
	signedup: string;
	activated: string;
	cancelled: string;
	converted: string;
	extended: string;
	limits: SubscriptionLimits;
	subscription: Subscription;
	profile: GroupProfile;
	cleanup: CleanupSettings;
	businessHours: OpeningHour[];
	bookingSettings: BookingSettings;
	closedDays: Holiday[];
	timezone: string;
	calendarTemplate: string;
	logo: string;
	archived: boolean;
	itemFlags: Flag[];
	orderLabels: ColoredLabel[];
	reservationLabels: ColoredLabel[];
	ticketLabels: ColoredLabel[];
	itemFields: CustomField[];
	kitFields: CustomField[];
	orderFields: CustomField[];
	reservationFields: CustomField[];
	customerFields: CustomField[];
	ticketFields: CustomField[];
	userRoleBlacklist: UserRoleBlacklistEntry[];
	itemTypes: ItemType[];
	ticketTypes: TicketType[];
}
